import { NativeEventListener } from "./api/NativeEventListener";
import { EventCallback } from "./core/contract/EventCallback";
import { InitializationCallback } from "./core/contract/InitializationCallback";
import { TrackerEngine } from "./core/engine/TrackerEngine";
import { TrackerConfig } from "./core/model/TrackerConfig";
import { TrackerConfiguration } from "./core/model/TrackerConfiguration";
import { TrackerEventType } from "./core/model/TrackerEventType";
import { TrackerVariableType } from "./core/model/TrackerVariableType";
import { TrackerModule } from "./di/TrackerModule";
import { Logger } from "./util/Logger";

/**
 * Main entry point for the Tracker Web SDK.
 * Mirrors Android TrackerSDK — Builder pattern, instance-based API.
 *
 * @example
 * ```ts
 * const tracker = new TrackerSDK.Builder()
 *   .setConfiguration({
 *     tenant: 'my-tenant',
 *     apiUrl: 'https://api.example.com',
 *     apiKey: 'my-api-key',
 *   })
 *   .build();
 *
 * tracker.initialize({
 *   onComplete(success, message) {
 *     if (success) console.log('SDK initialized');
 *   },
 * });
 * ```
 */
export class TrackerSDK {
  private readonly engine: TrackerEngine;
  private isInitialized = false;

  private constructor(configuration: TrackerConfiguration) {
    Logger.setEnabled(configuration.debug ?? false);
    const module = new TrackerModule(configuration);
    this.engine = module.getTrackerEngine();
  }

  /**
   * Initializes the Tracker SDK. Fetches configuration from backend,
   * sets up collectors, and starts event listeners.
   */
  async initialize(callback: InitializationCallback): Promise<void> {
    try {
      if (this.isInitialized) {
        callback.onComplete(true, "Tracker SDK already initialized");
        return;
      }

      await this.engine.initialize({
        onComplete: (success, message) => {
          if (success) {
            this.isInitialized = true;
          }
          callback.onComplete(success, message);
        },
      });
    } catch (error) {
      try {
        const message = error instanceof Error ? error.message : String(error);
        callback.onComplete(false, `Initialization failed: ${message}`);
      } catch {
        // Callback itself threw, nothing we can do
      }
    }
  }

  /** Checks if the SDK is initialized. */
  getIsInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Triggers a custom event asynchronously. The event will be queued
   * and sent when network is available.
   */
  triggerCustomEvent(eventName: string, args: Record<string, unknown> = {}): void {
    if (!this.isInitialized) return;
    try {
      this.engine.triggerCustomEvent(eventName, args);
    } catch {
      // Never crash on custom events
    }
  }

  /**
   * Triggers a custom event synchronously with a callback.
   * The callback will be invoked with the server response.
   */
  async triggerCustomEventSync(
    eventName: string,
    args: Record<string, unknown>,
    callback: EventCallback,
  ): Promise<void> {
    if (!this.isInitialized) {
      callback.onError(new Error("Tracker SDK not initialized"));
      return;
    }
    try {
      await this.engine.triggerCustomEventSync(eventName, args, callback);
    } catch (error) {
      try {
        callback.onError(error instanceof Error ? error : new Error(String(error)));
      } catch {
        // Callback threw
      }
    }
  }

  /** Sets a user-defined value that will be included in all events. */
  setUserDefinedValue(key: string, value: unknown): void {
    if (!this.isInitialized) return;
    this.engine.setUserDefinedValue(key, value);
  }

  /** Gets all user-defined values. */
  getUserDefinedValues(): Record<string, unknown> | null {
    if (!this.isInitialized) return null;
    return this.engine.getUserDefinedValues();
  }

  /** Gets all currently collected variables from all collectors. */
  getAllVariables(): Map<TrackerVariableType, unknown> | null {
    if (!this.isInitialized) return null;
    return this.engine.getAllVariables();
  }

  /** Gets the current tracker configuration from backend. */
  getConfiguration(): TrackerConfig | null {
    if (!this.isInitialized) return null;
    return this.engine.getConfiguration();
  }

  /** Triggers a native event programmatically (for testing/debugging). */
  triggerNativeEvent(eventType: TrackerEventType): void {
    if (!this.isInitialized) return;
    this.engine.triggerNativeEvent(eventType);
  }

  /** Sets a listener for native tracker events. */
  setNativeEventListener(listener: NativeEventListener | null): void {
    if (!this.isInitialized) return;
    this.engine.setNativeEventListener(listener);
  }

  /**
   * Terminates the SDK and releases all resources.
   * After calling this, you must re-create and re-initialize to use it again.
   */
  terminate(): void {
    if (!this.isInitialized) return;
    try {
      this.engine.terminate();
    } catch {
      // Log but continue
    } finally {
      this.isInitialized = false;
    }
  }

  /** Builder class for creating TrackerSDK instances. */
  static Builder = class TrackerSDKBuilder {
    configuration: TrackerConfiguration | null = null;

    setConfiguration(configuration: TrackerConfiguration): this {
      this.configuration = configuration;
      return this;
    }

    build(): TrackerSDK {
      if (!this.configuration) {
        throw new Error("Configuration must be set before building TrackerSDK");
      }
      return new TrackerSDK(this.configuration);
    }
  };
}
